
import { Coordinates, ElevationPoint } from "../types";

/**
 * Beregner avstand mellom to punkter i meter (Haversine).
 */
export const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  if (lat1 === undefined || lon1 === undefined || lat2 === undefined || lon2 === undefined) return 999999;
  if (isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) return 999999;
  
  const R = 6371e3; 
  const p1 = lat1 * Math.PI / 180;
  const p2 = lat2 * Math.PI / 180;
  const dp = (lat2 - lat1) * Math.PI / 180;
  const dl = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dp/2) * Math.sin(dp/2) + Math.cos(p1) * Math.cos(p2) * Math.sin(dl/2) * Math.sin(dl/2);
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
};

/**
 * Beregner bearing (retning i grader, 0=nord, 90=øst) fra punkt A til punkt B.
 */
export const getBearing = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const toRad = Math.PI / 180;
  const dLon = (lon2 - lon1) * toRad;
  const y = Math.sin(dLon) * Math.cos(lat2 * toRad);
  const x = Math.cos(lat1 * toRad) * Math.sin(lat2 * toRad) -
            Math.sin(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.cos(dLon);
  const bearing = Math.atan2(y, x) * (180 / Math.PI);
  return (bearing + 360) % 360;
};

/**
 * Sjekker om et punkt befinner seg inne i et polygon.
 * Bruker Ray-casting algoritme (Jordan curve theorem).
 */
export const isPointInPolygon = (lat: number, lon: number, polygon: {lat: number, lon: number}[]): boolean => {
  if (!polygon || polygon.length === 0) return false;
  let isInside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lat, yi = polygon[i].lon;
    const xj = polygon[j].lat, yj = polygon[j].lon;
    
    const intersect = ((yi > lon) !== (yj > lon)) &&
        (lat < (xj - xi) * (lon - yi) / (yj - yi) + xi);
    if (intersect) isInside = !isInside;
  }
  return isInside;
};

/**
 * Sjekker avstand til nærmeste punkt i en samling av geometrier.
 */
export const checkMinDistance = (lat: number, lon: number, targets: any[]): number => {
  let minDist = Infinity;
  for (const t of targets) {
    const geom = t.geometry || (t.center ? [t.center] : t.lat ? [{lat: t.lat, lon: t.lon}] : []);
    for (const p of geom) {
      if (!p || p.lat === undefined) continue;
      const d = getDistance(lat, lon, p.lat, p.lon);
      if (d < minDist) minDist = d;
    }
  }
  return minDist;
};

/**
 * Beregner korteste avstand fra et punkt til et linjestykke (segment).
 */
const distToSegment = (p: Coordinates, v: Coordinates, w: Coordinates): number => {
  const l2 = getDistance(v.latitude, v.longitude, w.latitude, w.longitude) ** 2;
  if (l2 === 0) return getDistance(p.latitude, p.longitude, v.latitude, v.longitude);
  
  // Konverter til tilnærmet kartesisk for projeksjon (forenklet for små avstander)
  // Dette er en approksimasjon, men rask nok for filtering.
  // For nøyaktig meter-kalkulasjon bruker vi getDistance på sluttresultatet.
  let t = ((p.latitude - v.latitude) * (w.latitude - v.latitude) + (p.longitude - v.longitude) * (w.longitude - v.longitude)) / 
          ((w.latitude - v.latitude) ** 2 + (w.longitude - v.longitude) ** 2);
          
  t = Math.max(0, Math.min(1, t));
  
  const projectionLat = v.latitude + t * (w.latitude - v.latitude);
  const projectionLon = v.longitude + t * (w.longitude - v.longitude);
  
  return getDistance(p.latitude, p.longitude, projectionLat, projectionLon);
};

/**
 * Beregner bounding box for en polyline. Bruk dette for å pre-beregne
 * bbox én gang utenfor filter-loops, og send inn som parameter til isPointNearPolyline.
 */
export interface PolylineBbox {
  latMin: number; latMax: number; lonMin: number; lonMax: number;
}

export const getPolylineBbox = (polyline: Coordinates[]): PolylineBbox => {
  let latMin = Infinity, latMax = -Infinity, lonMin = Infinity, lonMax = -Infinity;
  for (let i = 0; i < polyline.length; i++) {
    const lat = polyline[i].latitude;
    const lon = polyline[i].longitude;
    if (lat < latMin) latMin = lat;
    if (lat > latMax) latMax = lat;
    if (lon < lonMin) lonMin = lon;
    if (lon > lonMax) lonMax = lon;
  }
  return { latMin, latMax, lonMin, lonMax };
};

/**
 * Sjekker om et punkt er innenfor X meter av en polyline (rute).
 * Optimalisert ved å først sjekke bounding box for hele ruten.
 * Pass inn pre-beregnet bbox for å unngå O(n) rekalkulering per kall.
 */
export const isPointNearPolyline = (point: Coordinates, polyline: Coordinates[], maxMeters: number, bbox?: PolylineBbox): boolean => {
  if (polyline.length < 2) return false;

  // 1. Grovsortering: Er punktet i det hele tatt i nærheten av rutens bounding box?
  const bb = bbox || getPolylineBbox(polyline);
  const bufferDegrees = maxMeters / 111000; // Ca grader for buffer

  if (point.latitude < bb.latMin - bufferDegrees ||
      point.latitude > bb.latMax + bufferDegrees ||
      point.longitude < bb.lonMin - bufferDegrees ||
      point.longitude > bb.lonMax + bufferDegrees) {
    return false;
  }

  // 2. Finsjekk: Avstand til hvert segment
  for (let i = 0; i < polyline.length - 1; i++) {
    const dist = distToSegment(point, polyline[i], polyline[i+1]);
    if (dist <= maxMeters) return true;
  }

  return false;
};

/**
 * Beregner total lengde (km) for en GeoJSON FeatureCollection av LineStrings.
 */
export const calculateGeoJsonLength = (geoJson: any): number => {
  if (!geoJson || !geoJson.features) return 0;
  
  let totalMeters = 0;
  
  geoJson.features.forEach((feature: any) => {
    if (!feature.geometry || !feature.geometry.coordinates) return;
    
    // Håndter LineString
    if (feature.geometry.type === 'LineString') {
      const coords = feature.geometry.coordinates;
      for (let i = 0; i < coords.length - 1; i++) {
        // GeoJSON uses [lon, lat]
        totalMeters += getDistance(coords[i][1], coords[i][0], coords[i+1][1], coords[i+1][0]);
      }
    }
    // Håndter MultiLineString
    else if (feature.geometry.type === 'MultiLineString') {
      feature.geometry.coordinates.forEach((line: any[]) => {
        for (let i = 0; i < line.length - 1; i++) {
          totalMeters += getDistance(line[i][1], line[i][0], line[i+1][1], line[i+1][0]);
        }
      });
    }
  });
  
  return totalMeters / 1000;
};

/**
 * Finner et representativt midtpunkt for GeoJSON linjer for å plassere en etikett.
 * Returnerer det midterste punktet i det lengste segmentet.
 */
export const getGeoJsonLabelPoint = (geoJson: any): Coordinates | null => {
  if (!geoJson || !geoJson.features || geoJson.features.length === 0) return null;
  
  // Finn feature med flest koordinater (sannsynligvis den lengste synlige delen)
  let bestFeature = geoJson.features[0];
  let maxPoints = 0;
  
  geoJson.features.forEach((f: any) => {
    if (f.geometry?.type === 'LineString' && f.geometry.coordinates.length > maxPoints) {
      maxPoints = f.geometry.coordinates.length;
      bestFeature = f;
    }
  });
  
  if (!bestFeature.geometry?.coordinates || bestFeature.geometry.coordinates.length === 0) return null;
  
  const coords = bestFeature.geometry.coordinates;
  const midIndex = Math.floor(coords.length / 2);
  const pt = coords[midIndex];
  
  return { latitude: pt[1], longitude: pt[0] };
};

/**
 * Konverterer GeoJSON Features (fra rutebygger) til en GPX streng.
 * Støtter flere features, FeatureCollections og nøstede strukturer.
 */
export const geoJsonToGpx = (features: any[], name: string = "Ny Rute"): string => {
  let segments = '';
  
  // Flater ut arrayet slik at vi håndterer både enkelt-features og FeatureCollections i samme loop
  const flatFeatures: any[] = [];
  
  features.forEach(item => {
    if (item.type === 'FeatureCollection' && item.features) {
      flatFeatures.push(...item.features);
    } else {
      flatFeatures.push(item);
    }
  });

  flatFeatures.forEach(feature => {
    if (!feature.geometry || !feature.geometry.coordinates) return;

    if (feature.geometry.type === 'LineString') {
      let points = '';
      feature.geometry.coordinates.forEach((coord: number[]) => {
        // GeoJSON er [lon, lat], GPX er lat="..." lon="..."
        if (coord.length >= 2 && !isNaN(coord[0]) && !isNaN(coord[1])) {
           points += `<trkpt lat="${coord[1]}" lon="${coord[0]}"></trkpt>`;
        }
      });
      if (points) segments += `<trkseg>${points}</trkseg>`;
    }
    // Støtte for MultiLineString (f.eks. veier med gap)
    else if (feature.geometry.type === 'MultiLineString') {
      feature.geometry.coordinates.forEach((line: any[]) => {
         let points = '';
         line.forEach((coord: number[]) => {
            if (coord.length >= 2 && !isNaN(coord[0]) && !isNaN(coord[1])) {
               points += `<trkpt lat="${coord[1]}" lon="${coord[0]}"></trkpt>`;
            }
         });
         if (points) segments += `<trkseg>${points}</trkseg>`;
      });
    }
  });

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Fricamp Rutebygger" xmlns="http://www.topografix.com/GPX/1/1">
  <trk>
    <name>${name}</name>
    ${segments}
  </trk>
</gpx>`;
};

/**
 * Beregner arealet av et polygon i kvadratmeter (Shoelace-formel med sfaerisk korreksjon).
 * Koordinater er [lat, lon] par.
 */
export const calculatePolygonArea = (coords: [number, number][]): number => {
  if (coords.length < 3) return 0;
  const R = 6371e3;
  let area = 0;
  for (let i = 0; i < coords.length; i++) {
    const j = (i + 1) % coords.length;
    const lat1 = coords[i][0] * Math.PI / 180;
    const lat2 = coords[j][0] * Math.PI / 180;
    const dLon = (coords[j][1] - coords[i][1]) * Math.PI / 180;
    area += dLon * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  return Math.abs(area * R * R / 2);
};

/**
 * Formaterer avstand i meter til lesbar streng (m eller km).
 */
export const formatDistance = (meters: number): string => {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
};

/**
 * Formaterer areal i kvadratmeter til lesbar streng (m2, dekar eller km2).
 * Bruker norsk standard (dekar = 1000 m2).
 */
export const formatArea = (sqMeters: number): string => {
  if (sqMeters < 10000) return `${Math.round(sqMeters)} m\u00B2`;
  if (sqMeters < 1000000) return `${(sqMeters / 10000).toFixed(1)} daa`;
  return `${(sqMeters / 1000000).toFixed(2)} km\u00B2`;
};

/**
 * Maps a lat/lon point to distance (km) along a route by finding the closest
 * coordinate and mapping proportionally to the elevation profile.
 * Returns null if the point is > 2km from the route.
 */
export const getDistanceAlongRoute = (
  pointLat: number,
  pointLon: number,
  routeCoords: [number, number][],
  profile: ElevationPoint[]
): number | null => {
  if (routeCoords.length === 0 || profile.length === 0) return null;

  let minDist = Infinity;
  let closestIdx = 0;

  for (let i = 0; i < routeCoords.length; i++) {
    const d = getDistance(pointLat, pointLon, routeCoords[i][0], routeCoords[i][1]);
    if (d < minDist) {
      minDist = d;
      closestIdx = i;
    }
  }

  if (minDist > 2000) return null;

  const profileIdx = Math.round((closestIdx / Math.max(1, routeCoords.length - 1)) * (profile.length - 1));
  const clampedIdx = Math.max(0, Math.min(profile.length - 1, profileIdx));
  return profile[clampedIdx].distance;
};
